import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Link, Typography } from "@mui/material";
import { ChevronRight as ChevronRightIcon, ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { Button } from "@/components/Button";
import { Checkbox } from "@/components/Checkbox";
import { PasswordValidationInput } from "@/components/PasswordValidationInput";
import { Layout } from "@/pages/Signup/Layout";
import React, { ChangeEvent, useState } from "react";
import { useForm } from "react-hook-form";
import { EXTERNAL_URLS } from "@/routes/routePaths";
import { ZodError } from "zod";
import { PasswordErrors, PasswordFormData, passwordErrorsDefault, passwordSchema } from "./types";
import { useSignUpContext } from "../../context";
import LexterLogoIcon from "@/assets/svgs/lexter-logo.svg?react";
import { PasswordField } from "@/components/PasswordField";

export const PasswordForm = () => {
  const { submitData, isLoading, previousStep } = useSignUpContext();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
  });
  const [passwordErrors, setPasswordErrors] = useState<PasswordErrors>(passwordErrorsDefault);

  const handlePasswordChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let newPasswordErrors = passwordErrorsDefault;

    try {
      passwordSchema.parse({
        password: event.currentTarget.value,
      });
    } catch (error) {
      const passwordIssues = (error as ZodError).issues.filter((issue) => issue.path[0] === "password");
      const passwordIssuesByMessage = passwordIssues.reduce(
        (acc, issue) => ({ ...acc, [issue.message]: true }),
        {} as PasswordErrors
      );

      newPasswordErrors = {
        ...newPasswordErrors,
        ...passwordIssuesByMessage,
      };
    }

    setPasswordErrors(newPasswordErrors);
  };

  return (
    <Layout>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
          overflow: "auto",
        }}
        component="form"
        onSubmit={handleSubmit(submitData)}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            textAlign: "center",
            gap: 3,
            mb: 2,
          }}
        >
          <Box
            component={LexterLogoIcon}
            height={54}
            sx={{
              color: "primary.main",
            }}
          />
          <Typography variant="body1">Vamos precisar de alguns dados para completar o seu cadastro.</Typography>
        </Box>

        <PasswordValidationInput
          validations={[
            { text: "uma letra", valid: !passwordErrors.letter },
            { text: "um número ou caractere especial", valid: !passwordErrors.number_or_special },
            { text: "mínimo de 8 caracteres", valid: !passwordErrors.min },
          ]}
          label="Senha"
          placeholder="Crie uma senha"
          {...register("password")}
          onChange={handlePasswordChange}
          error={!!errors.password || passwordErrors.max}
          helperText={passwordErrors.max ? "Máximo de 200 caracteres." : undefined}
        />

        <PasswordField
          label="Confirmar senha"
          placeholder="Digite novamente sua senha"
          {...register("confirmPassword")}
          error={!!errors.confirmPassword}
          helperText={errors.confirmPassword?.message}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "0.5rem",
          }}
        >
          <Checkbox
            {...register("terms")}
            error={!!errors.terms}
            helperText={errors.terms?.message}
            label={
              <Typography variant="multiLineBody">
                Estou de acordo com a{" "}
                <Link href={EXTERNAL_URLS.PRIVACY_POLICY} target="_blank" style={{ fontWeight: "700" }}>
                  Política de Privacidade
                </Link>{" "}
                da Lexter.Ai.
              </Typography>
            }
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <Button type="submit" variant="contained" fullWidth isLoading={isLoading} endIcon={ChevronRightIcon}>
            Continuar
          </Button>
          <Button
            onClick={() => previousStep()}
            startIcon={ArrowBackIcon}
            type="button"
            variant="text"
            sx={{
              color: "common.black",
            }}
            fullWidth
          >
            Voltar
          </Button>
        </Box>
      </Box>
    </Layout>
  );
};
